import * as React from 'react';

import {ActionBox} from 'shells/action-box/action-box';
import {Button} from 'shells/button/button';
import {Card} from 'shells/card/card';
import {colors} from 'shells/colors/colors';
import {Stack} from 'shells/layout/stack/stack';
import {Body} from 'shells/typography/body';

import type {ExternalServiceType} from 'nutshell-graphql-types';
import type {GetSessionUser_loggedInUser as User} from 'nutshell-graphql-types';

import {TranscriptionModePicker} from '../external-meetings/transcription-mode-picker';
import {CallActivityTypeSelect} from '../setup/phone/call-activity-type-select';

import {getActivityTypeSettingName, getServiceNoun, getServiceTypeIconComponent} from './helpers';

type Props = {
    title: string;
    tertiaryInformation: string;
    externalUserIdentifier: string;
    integrationType: ExternalServiceType;
    defaultCallActivityTypeId: string | null | undefined;
    defaultVoicemailActivityTypeId: string | null | undefined;
    refetch: () => Promise<any>;
    onDisable: () => void;
    user: User | null | undefined;
};

export function SetupCard(props: Props) {
    const [isDisabling, setIsDisabling] = React.useState<boolean>(false);
    const Icon = getServiceTypeIconComponent(props.integrationType);

    const disableText = isDisabling ? 'Disabling…' : 'Disable';

    return (
        <Card hasBorder={true} fitHeight={true} overflowVisible={true}>
            <ActionBox
                icon={
                    <Icon
                        size={32}
                        fill={props.integrationType === 'CLICKTOCALL' ? colors.orange : undefined}
                    />
                }
                title={props.title}
                subtitle={
                    <Stack spacing={4}>
                        <Body size='small' color='dark'>
                            {props.externalUserIdentifier}
                        </Body>
                        <Body size='small' color='dark'>
                            {props.tertiaryInformation}
                        </Body>
                    </Stack>
                }
                actionElement={
                    <Button
                        variant='text-danger'
                        size='small'
                        onClick={() => {
                            setIsDisabling(true);
                            props.onDisable();
                        }}
                    >
                        {disableText}
                    </Button>
                }
                isBorderless={true}
            />
            <div className='px-16 pb-16 flex flex-dir-col gap-24'>
                {props.integrationType !== 'CLICKTOCALL' && props.user && (
                    <Stack spacing={12}>
                        <TranscriptionModePicker
                            serviceType={props.integrationType}
                            user={props.user}
                        />
                    </Stack>
                )}
                <CallActivityTypeSelect
                    label={
                        <div className='pb-8'>
                            <Body isBold={true} size='small' color='dark'>
                                Which activity type would you like{' '}
                                {getServiceNoun(props.integrationType)} calls to be logged as?
                            </Body>
                        </div>
                    }
                    activityTypeId={props.defaultCallActivityTypeId}
                    settingName={getActivityTypeSettingName(props.integrationType, false) || ''}
                    refetch={props.refetch}
                    isHorizontal={false}
                    autoFocus={false}
                />
                {(props.integrationType === 'RINGCENTRAL' || props.integrationType === 'KIXIE') && (
                    <CallActivityTypeSelect
                        label={
                            <div className='pb-8'>
                                <Body isBold={true} size='small' color='dark'>
                                    Which activity type would you like{' '}
                                    {getServiceNoun(props.integrationType)} voicemails to be logged
                                    as?
                                </Body>
                            </div>
                        }
                        activityTypeId={props.defaultVoicemailActivityTypeId}
                        settingName={getActivityTypeSettingName(props.integrationType, true) || ''}
                        refetch={props.refetch}
                        isHorizontal={false}
                        autoFocus={false}
                    />
                )}
            </div>
        </Card>
    );
}
